import axios, { AxiosInstance, AxiosResponse } from 'axios';

// API Configuration
export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8002/api/v1';
export const WS_BASE_URL = import.meta.env.VITE_WS_BASE_URL || 'ws://localhost:8002';

// Create axios instance with default config
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle auth errors
apiClient.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// API endpoints
export const API_ENDPOINTS = {
  // Authentication
  AUTH: {
    LOGIN: '/auth/login',
    REFRESH: '/auth/refresh',
    ME: '/auth/me',
  },
  
  // Accounts
  ACCOUNTS: {
    LIST: '/accounts/',
    DETAIL: (id: string) => `/accounts/${id}`,
    DETAILS: (id: string) => `/accounts/${id}/details`,
    TRADES: (id: string) => `/accounts/${id}/trades`,
    PERFORMANCE: (id: string) => `/accounts/${id}/performance`,
    INVESTORS: (id: string) => `/accounts/${id}/investors`,
  },

  // Clients (LPs / investors)
  CLIENTS: {
    LIST: '/clients/',
    DETAIL: (id: string) => `/clients/${id}`,
    ALLOCATIONS: (id: string) => `/clients/${id}/allocations`,
    CASHFLOWS: (id: string) => `/clients/${id}/cashflows`,
    CREATE: '/clients/',
    UPDATE: (id: string) => `/clients/${id}`,
    DEPOSIT: '/clients/cashflows/deposit',
    WITHDRAW: '/clients/cashflows/withdraw',
    REVERSE: (cashflowId: string) => `/clients/cashflows/${cashflowId}/reverse`,
  },

  // Trades
  TRADES: {
    LIST: '/trades/',
    ONGOING: '/trades/ongoing',
    SUMMARY: '/trades/summary',
  },
  
  // News
  NEWS: {
    LIST: '/news/',
  },
  
  // Market Regime
  MARKET_REGIME: {
    CURRENT: '/market-regime/current',
    HISTORY: '/market-regime/history',
  },
  
  // Analytics
  ANALYTICS: {
    OVERVIEW: '/analytics/overview',
    DAILY: '/analytics/daily',
  },
  
  // Monitoring
  MONITORING: {
    SERVER: '/monitoring/server',
    DATABASE: '/monitoring/database',
    GRAFANA_URL: '/monitoring/grafana-embed-url',
  },

  // Infrastructure
  INFRASTRUCTURE: {
    AGENTS: '/infrastructure/agents',
    METRICS_REALTIME: '/infrastructure/metrics/realtime',
    STATUS: '/infrastructure/status',
  },

  // Admin (admin only)
  ADMIN: {
    USERS: '/admin/users',
    USER: (id: string) => `/admin/users/${id}`,
    ACCOUNTS: '/admin/accounts',
    ACCOUNT_DELETE: (id: string) => `/admin/accounts/${id}`,
    ACCOUNT_RESTORE: (id: string) => `/admin/accounts/${id}/restore`,
  },

  // Data Fetching
  DATA: {
    ACCOUNTS: '/data/accounts',
    TRADES: '/data/trades',
    DAILY_STATS: '/data/daily-statistics',
  },

  // Observability (legacy — kept for Ecosystem page compatibility)
  OBSERVABILITY: {
    METRICS: '/observability/metrics',
    METRICS_BY_CATEGORY: (category: string) => `/observability/metrics/${category}`,
    METRICS_BY_BOT: (category: string, bot: string) => `/observability/metrics/${category}/${bot}`,
  },

  // Bots
  BOTS: {
    LIST: '/bots/',
    DETAIL: (id: string) => `/bots/${id}`,
    METRICS: (id: string) => `/bots/${id}/metrics`,
    LOGS: (id: string) => `/bots/${id}/logs`,
    TRADE_STATS: (id: string) => `/bots/${id}/trade-stats`,
    TRADES: (id: string) => `/bots/${id}/trades`,
  },

  // Logs
  LOGS: {
    LIST: '/logs/',
    APPS: '/logs/apps',
  },

  // Apps directory (tailnet pin-board)
  APPS: {
    LIST: '/apps',
    DETAIL: (id: string) => `/apps/${id}`,
  },

  // Market Regime Indicators
  INDICATORS: {
    LATEST: '/indicators/latest',
    LATEST_TYPE: (type: string) => `/indicators/latest/${type}`,
    SUMMARY: '/indicators/summary',
    HISTORY: (type: string) => `/indicators/history/${type}`,
  },
} as const;

// Query keys for React Query
export const QUERY_KEYS = {
  accounts: ['accounts'] as const,
  account: (id: string) => ['account', id] as const,
  accountDetails: (id: string) => ['account', id, 'details'] as const,
  accountTrades: (id: string) => ['account', id, 'trades'] as const,
  accountPerformance: (id: string) => ['account', id, 'performance'] as const,
  accountInvestors: (id: string) => ['account', id, 'investors'] as const,
  clients: {
    list: ['clients'] as const,
    detail: (id: string) => ['clients', id] as const,
    allocations: (id: string) => ['clients', id, 'allocations'] as const,
    cashflows: (id: string) => ['clients', id, 'cashflows'] as const,
  },
  trades: {
    ongoing: ['trades', 'ongoing'] as const,
    summary: (filters?: any) => ['trades', 'summary', filters] as const,
  },
  news: (filters?: any) => ['news', filters] as const,
  marketRegime: {
    current: ['market-regime', 'current'] as const,
    history: (filters?: any) => ['market-regime', 'history', filters] as const,
  },
  analytics: {
    overview: (filters?: any) => ['analytics', 'overview', filters] as const,
    daily: (filters?: any) => ['analytics', 'daily', filters] as const,
  },
  monitoring: ['monitoring'] as const,
  infrastructure: {
    agents: ['infrastructure', 'agents'] as const,
    metrics: ['infrastructure', 'metrics'] as const,
    status: ['infrastructure', 'status'] as const,
  },
  database: ['monitoring', 'database'] as const,
  admin: {
    users: ['admin', 'users'] as const,
    accounts: ['admin', 'accounts'] as const,
  },
  data: {
    accounts: (filters?: any) => ['data', 'accounts', filters] as const,
    trades: (filters?: any) => ['data', 'trades', filters] as const,
    dailyStats: (filters?: any) => ['data', 'daily-stats', filters] as const,
  },
  observability: {
    metrics: ['observability', 'metrics'] as const,
    metricsByCategory: (category: string) => ['observability', 'metrics', category] as const,
    metricsByBot: (category: string, bot: string) => ['observability', 'metrics', category, bot] as const,
  },
  bots: {
    list: ['bots'] as const,
    detail: (id: string) => ['bots', id] as const,
    metrics: (id: string) => ['bots', id, 'metrics'] as const,
    logs: (id: string) => ['bots', id, 'logs'] as const,
    tradeStats: (id: string, range?: string) => ['bots', id, 'trade-stats', range] as const,
    trades: (id: string, filters?: any) => ['bots', id, 'trades', filters] as const,
  },
  logs: {
    list: (app?: string) => ['logs', app] as const,
    apps: ['logs', 'apps'] as const,
  },
  apps: {
    list: ['apps'] as const,
    detail: (id: string) => ['apps', id] as const,
  },
  indicators: {
    latest: ['indicators', 'latest'] as const,
    latestType: (type: string) => ['indicators', 'latest', type] as const,
    summary: ['indicators', 'summary'] as const,
    history: (type: string) => ['indicators', 'history', type] as const,
  },
} as const;

export default apiClient;
